import React, { useRef, useEffect, useState, useCallback } from 'react';
import { FaTimes, FaExpand, FaCompress, FaUsers, FaSignOutAlt, FaPhone, FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash } from 'react-icons/fa';
import styles from './JitsiMeeting.module.css';

const JitsiMeeting = ({
  roomName,
  displayName,
  email,
  isHost = false,
  onLeave,
  onEndConference,
  participants = [],
  onParticipantJoined,
  onParticipantLeft
}) => {
  const apiRef = useRef(null);
  const containerRef = useRef(null);
  const jitsiContainerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [showParticipants, setShowParticipants] = useState(false);
  const [jitsiParticipants, setJitsiParticipants] = useState([]);
  const [isApiReady, setIsApiReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);

  // Load Jitsi External API script and initialize
  useEffect(() => {
    // Create a unique room name - use a public server without lobby
    const sanitizedRoomName = `Habcare${roomName.replace(/[^a-zA-Z0-9]/g, '')}`;
    
    const loadJitsiScript = () => {
      return new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) {
          resolve();
          return;
        }
        
        // Use jitsi.riot.im which doesn't have lobby enabled
        const script = document.createElement('script');
        script.src = 'https://jitsi.riot.im/external_api.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const initJitsi = async () => {
      try {
        await loadJitsiScript();
        
        if (jitsiContainerRef.current && !apiRef.current) {
          // Use jitsi.riot.im - a public Jitsi server without lobby
          const domain = 'jitsi.riot.im';
          const options = {
            roomName: sanitizedRoomName,
            parentNode: jitsiContainerRef.current,
            userInfo: {
              displayName: displayName || 'Katılımcı',
              email: email || '',
            },
            configOverwrite: {
              startWithAudioMuted: false,
              startWithVideoMuted: false,
              disableDeepLinking: true,
              prejoinPageEnabled: false,
              prejoinConfig: { enabled: false },
              enableWelcomePage: false,
              enableClosePage: false,
              disableInviteFunctions: true,
              hideConferenceSubject: true,
              hideConferenceTimer: false,
              enableInsecureRoomNameWarning: false,
              disableModeratorIndicator: true,
              notifications: [],
              toolbarButtons: [
                'microphone',
                'camera',
                'desktop',
                'chat',
                'raisehand',
                'tileview',
                'fullscreen',
              ],
              subject: 'Habcare Toplantı',
              defaultLanguage: 'tr',
            },
            interfaceConfigOverwrite: {
              SHOW_JITSI_WATERMARK: false,
              SHOW_WATERMARK_FOR_GUESTS: false,
              SHOW_BRAND_WATERMARK: false,
              SHOW_POWERED_BY: false,
              SHOW_PROMOTIONAL_CLOSE_PAGE: false,
              MOBILE_APP_PROMO: false,
              HIDE_INVITE_MORE_HEADER: true,
              TOOLBAR_ALWAYS_VISIBLE: true,
              TOOLBAR_TIMEOUT: 20000,
              DEFAULT_BACKGROUND: '#1a1a2e',
              DEFAULT_REMOTE_DISPLAY_NAME: 'Katılımcı',
              DEFAULT_LOCAL_DISPLAY_NAME: 'Ben',
              DISABLE_VIDEO_BACKGROUND: false,
              GENERATE_ROOMNAMES_ON_WELCOME_PAGE: false,
              DISPLAY_WELCOME_PAGE_CONTENT: false,
              DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT: false,
              SHOW_CHROME_EXTENSION_BANNER: false,
              LANG_DETECTION: false,
              DEFAULT_LANGUAGE: 'tr',
            },
          };

          const api = new window.JitsiMeetExternalAPI(domain, options);
          apiRef.current = api;
          setIsApiReady(true);

          // Event listeners
          api.addListener('participantJoined', (participant) => {
            console.log('Jitsi: Participant joined', participant);
            setJitsiParticipants(prev => [...prev, participant]);
            onParticipantJoined?.(participant);
          });

          api.addListener('participantLeft', (participant) => {
            console.log('Jitsi: Participant left', participant);
            setJitsiParticipants(prev => prev.filter(p => p.id !== participant.id));
            onParticipantLeft?.(participant);
          });

          api.addListener('videoConferenceJoined', (data) => {
            console.log('Jitsi: Conference joined', data);
          });

          api.addListener('videoConferenceLeft', () => {
            console.log('Jitsi: Conference left');
            onLeave?.();
          });

          api.addListener('readyToClose', () => {
            console.log('Jitsi: Ready to close');
            onLeave?.();
          });

          api.addListener('audioMuteStatusChanged', (data) => {
            setIsMuted(data.muted);
          });

          api.addListener('videoMuteStatusChanged', (data) => {
            setIsVideoOff(data.muted);
          });
        }
      } catch (error) {
        console.error('Failed to load Jitsi:', error);
      }
    };

    initJitsi();

    // Cleanup
    return () => {
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
  }, [roomName, displayName, email, onLeave, onParticipantJoined, onParticipantLeft]);

  // Call duration timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.executeCommand('toggleAudio');
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.executeCommand('toggleVideo');
    }
  }, []);

  // Handle leave
  const handleLeave = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.executeCommand('hangup');
    }
    onLeave?.();
  }, [onLeave]);

  // Handle end conference (host only)
  const handleEndConferenceClick = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.executeCommand('hangup');
    }
    onEndConference?.();
  }, [onEndConference]);

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Combined participants count (from Firestore + Jitsi)
  const totalParticipants = Math.max(participants.length, jitsiParticipants.length + 1);

  return (
    <div className={styles.jitsiContainer} ref={containerRef}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <div className={styles.meetingInfo}>
          <span className={styles.recordIndicator}>●</span>
          <span className={styles.meetingDuration}>{formatDuration(callDuration)}</span>
        </div>
        <div className={styles.meetingTitle}>Toplantı</div>
        <div className={styles.topBarActions}>
          <button 
            className={`${styles.topBarButton} ${showParticipants ? styles.active : ''}`}
            onClick={() => setShowParticipants(!showParticipants)}
            title="Katılımcılar"
          >
            <FaUsers />
            <span className={styles.participantBadge}>{totalParticipants}</span>
          </button>
          <button 
            className={styles.topBarButton}
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
          >
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={styles.mainArea}>
        {/* Jitsi Meeting Container */}
        <div className={styles.jitsiWrapper} ref={jitsiContainerRef} />

        {/* Participants Panel */}
        {showParticipants && (
          <div className={styles.participantsPanel}>
            <div className={styles.panelHeader}>
              <h3>Katılımcılar ({totalParticipants})</h3>
              <button onClick={() => setShowParticipants(false)} className={styles.panelClose}>
                <FaTimes />
              </button>
            </div>
            <div className={styles.panelContent}>
              {/* Firebase participants list */}
              {participants.map((participant, index) => {
                const name = typeof participant === 'object' 
                  ? (participant.name || participant.email?.split('@')[0]) 
                  : (participant?.includes('@') ? participant.split('@')[0] : participant);
                const participantEmail = typeof participant === 'object' ? participant.email : participant;
                const isCurrentUser = participantEmail === email;
                
                return (
                  <div key={participantEmail || index} className={styles.participantRow}>
                    <div className={styles.participantInfo}>
                      <div className={styles.smallAvatar}>
                        {getInitials(name)}
                      </div>
                      <span>{isCurrentUser ? 'Siz' : name}</span>
                    </div>
                    {isHost && participantEmail === email && (
                      <span className={styles.hostLabel}>Sunucu</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Control Bar */}
      <div className={styles.controlBar}>
        <div className={styles.controlsCenter}>
          <button
            className={`${styles.controlBtn} ${isMuted ? styles.off : ''}`}
            onClick={toggleMute}
            title={isMuted ? 'Mikrofonu aç' : 'Mikrofonu kapat'}
          >
            {isMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
          </button>

          <button
            className={`${styles.controlBtn} ${isVideoOff ? styles.off : ''}`}
            onClick={toggleVideo}
            title={isVideoOff ? 'Kamerayı aç' : 'Kamerayı kapat'}
          >
            {isVideoOff ? <FaVideoSlash /> : <FaVideo />}
          </button>

          <button
            className={`${styles.controlBtn} ${styles.leaveBtn}`}
            onClick={isHost ? handleEndConferenceClick : handleLeave}
            title={isHost ? 'Toplantıyı bitir' : 'Ayrıl'}
          >
            {isHost ? <FaPhone /> : <FaSignOutAlt />}
            <span className={styles.leaveBtnText}>{isHost ? 'Bitir' : 'Ayrıl'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default JitsiMeeting;
